/**
 * Use the hero rail to feature 5+ promoted stories that are related. For example, Beauty steals during a major event. Use when a higher prominence and/or creative image is needed.
 *
 * @module views/components/FullBleedHeroCompact
 * @memberof -Common
 */
import './FullBleedHeroCompact.scss';

import React, { useRef } from 'react';

import classnames from 'classnames';
import PropTypes from 'prop-types';

import ActionGroups from '@ulta/core/components/ActionGroups/ActionGroups';
import GridContainer from '@ulta/core/components/GridContainer/GridContainer';
import Link_Huge from '@ulta/core/components/Link_Huge/Link_Huge';
import Text from '@ulta/core/components/Text/Text';
import { useIntersectionObserver } from '@ulta/core/hooks/useIntersectionObserver/useIntersectionObserver';
import { useDeviceInflection } from '@ulta/core/providers/InflectionProvider/InflectionProvider';
import { spreadProps } from '@ulta/core/utils/dom/dom';
import { getUltaAssetHtml } from '@ulta/core/utils/getUltaAssetHtml/getUltaAssetHtml';
import { handleIntersection } from '@ulta/core/utils/intersectionProcessor/intersectionProcessor';

import Tag from '@ulta/modules/Tag/Tag';

import constants from '@ulta/utils/constants/constants';

/**
 * Represents a FullBleedHeroCompact component
 *
 * @method
 * @param {FullBleedHeroCompactProps} props - React properties passed from composition
 * @returns FullBleedHeroCompact
 */
export const FullBleedHeroCompact = function( props ){
  const { breakpoint } = useDeviceInflection();
  const FullBleedHeroCompactRef = useRef( null );

  const {
    image,
    mobileImage,
    tabletImage,
    actionGroups,
    body,
    bodySecondLine,
    eyebrow,
    headline,
    headlineTag = defaultProps.headlineTag,
    tag,
    textColor,
    textPosition,
    root = defaultProps.root,
    rootMargin = defaultProps.rootMargin,
    threshold = defaultProps.threshold
  } = props;

  let formattedMedia;

  /** Breakpoint settings for incoming media */
  const isMobile = breakpoint?.isSmallDevice();
  const isDesktop = breakpoint && ( breakpoint.CURRENT_BREAKPOINT === 'LG' || breakpoint.CURRENT_BREAKPOINT === 'XL' );
  const isTablet = breakpoint && breakpoint.CURRENT_BREAKPOINT === 'MD';

  const hasText = body || eyebrow || headline || actionGroups || tag;
  const hasTag = tag?.message || tag?.variant === 'urgency';
  const hasActions = actionGroups?.actions?.length > 0;
  const hasOneAction =  actionGroups?.actions?.length === 1;
  const hasImage = image || mobileImage || tabletImage;

  /** Formatters for incoming media */
  if( hasImage ){
    formattedMedia = getUltaAssetHtml( {
      image: image,
      mobileImage: mobileImage,
      tabletImage: tabletImage,
      isMobile: isMobile,
      isDesktop: isDesktop,
      isTablet: isTablet
    } );
  }

  const textAlignment = textPosition === 'center-center' || textPosition === 'top-center' || textPosition === 'bottom-center' ? 'center' : 'left';

  useIntersectionObserver(
    FullBleedHeroCompactRef,
    {
      root: root,
      rootMargin: rootMargin,
      threshold: threshold
    },
    handleIntersection( props )
  );

  if( !hasImage ){
    return null;
  }

  const componentClassNameMain = classnames( 'FullBleedHeroCompact__mainContainer', {
    [`FullBleedHeroCompact__contentAlign--${textPosition}`]: textPosition
  } );

  return (
    <div className='FullBleedHeroCompact' ref={ FullBleedHeroCompactRef }>
      { hasOneAction && (
        <Link_Huge
          className='FullBleedHeroCompact__fullyClickableParent'
          ariaLabel={ actionGroups.actions[0]?.label }
          action={ actionGroups.actions[0] }
          tabIndex={ -1 }
        ></Link_Huge>
      ) }
      { hasImage && (
        <div className='FullBleedHeroCompact__image'>{ formattedMedia }</div>
      ) }
      { hasText && (
        <div className={ componentClassNameMain }>
          <GridContainer>
            <div className='FullBleedHeroCompact__bodyAndAction'>
              <div className='FullBleedHeroCompact__body'>
                { hasTag && (
                  <div
                    className={ classnames( 'FullBleedHeroCompact__tag', {
                      [`FullBleedHeroCompact__tag--${textAlignment}`]: textAlignment
                    } ) }
                  >
                    <Tag
                      { ...spreadProps( { ...tag } ) }
                      icon={ tag.variant === 'urgency' ? 'clock' : tag.icon }
                    />
                  </div>
                ) }
                { eyebrow && (
                  <Text
                    textStyle='eyebrow'
                    htmlTag='span'
                    textAlign={ textAlignment }
                    color={ textColor }
                  >
                    { eyebrow }
                  </Text>
                ) }

                { headline && (
                  <Text
                    textStyle='display-sm'
                    textAlign={ textAlignment }
                    color={ textColor }
                    htmlTag={ headlineTag }
                  >
                    { headline }
                  </Text>
                ) }
                { body && (
                  <Text
                    textStyle='body-xlg'
                    htmlTag='p'
                    textAlign={ textAlignment }
                    color={ textColor }
                  >
                    { body }
                  </Text>
                ) }
                { bodySecondLine && (
                  <Text
                    textStyle='body-xlg'
                    htmlTag='p'
                    textAlign={ textAlignment }
                    color={ textColor }
                  >
                    { bodySecondLine }
                  </Text>
                ) }
              </div>
              { hasActions && (
                <div
                  className={ classnames( 'FullBleedHeroCompact__action', {
                    [`FullBleedHeroCompact__action--${textAlignment}`]: textAlignment
                  } ) }
                >
                  <ActionGroups
                    actions={ actionGroups?.actions }
                    style={ actionGroups?.style }
                    types={ actionGroups?.types }
                    variant={ actionGroups?.variant }
                    alignment={ textAlignment }
                  />
                </div>
              ) }
            </div>
          </GridContainer>
        </div>
      ) }
    </div>
  );
};

/**
 * Property type definitions
 * @typedef FullBleedHeroCompactProps
 * @type {object}
 * @property {object} actionGroups - Sets actions for the component.
 * @property {string} backgroundColor - Sets the background color.
 * @property {string} body - Sets the body text.
 * @property {string} bodySecondLine - Sets the second line of body text.
 * @property {string} eyebrow - Sets the eyebrow text.
 * @property {string} headline - Sets the headline text.
 * @property {string} headlineTag - Sets the HTML tag for the headline.
 * @property {object} image - Sets the desktop image.
 * @property {object} mobileImage - Sets the mobile image.
 * @property {object} tabletImage - Sets the tablet image.
 * @property {object} tag - Sets the tag properties.
 * @property {string} textColor - Sets the text color.
 * @property {string} textPosition - Sets the text alignment.
 * @property {string} variant - Sets the component variant.
 */
export const propTypes = {
  actionGroups: PropTypes.object,
  backgroundColor: PropTypes.string,
  body: PropTypes.string,
  bodySecondLine: PropTypes.string,
  eyebrow: PropTypes.string,
  headline: PropTypes.string,
  headlineTag: PropTypes.string,
  image: PropTypes.object,
  mobileImage: PropTypes.object,
  tag: PropTypes.object,
  tabletImage: PropTypes.object,
  textColor: PropTypes.string,
  textPosition: PropTypes.string,
  variant: PropTypes.string
};

/**
 * Default values for passed properties
 * @type object
 * @property {string} headlineTag - Default headlineTag is h2
 */
export const defaultProps = {
  headlineTag: 'h2',
  ...constants.INTERSECTION_OBSERVER_OPTIONS
};

FullBleedHeroCompact.defaultProps = defaultProps;
FullBleedHeroCompact.propTypes = propTypes;

export default FullBleedHeroCompact;